import { GlobalService } from '../global/services/global.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { AppService } from '../app.service';

/**
 * Shared extraction for CE → standalone migration (GlobalService / WorkspaceService / AppService).
 * Keeps filter cache array handling and row payload fallbacks consistent with migration-docs gates.
 */
export function extractObject(input: any): any {
  if (input === null || input === undefined) {
    return {};
  }
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof input === 'object' && !Array.isArray(input) ? input : {};
}

export function normalizeFilterParams(activeScreen: any): any {
  const raw =
    activeScreen?.filters?.getFilterParamsWithLabels?.() ??
    activeScreen?.filters?.getFilterParams?.() ??
    activeScreen?.filters?.getFilterParamsFromCache?.() ??
    {};
  if (Array.isArray(raw) && raw.length > 0) {
    return extractObject(raw[0]);
  }
  return extractObject(raw);
}

/** Row / grid selection: may be an object map or an array of selected rows (e.g. admin grids). */
export function rowPayloadFromActiveScreen(activeScreen: any): any {
  const activeData = activeScreen?.data || {};
  const raw = activeData?.selected?.rowData ?? activeData?.rowData ?? activeData;
  if (Array.isArray(raw)) {
    return raw;
  }
  return extractObject(raw);
}

export function gridPayloadFromActiveScreen(activeScreen: any): any {
  const activeData = activeScreen?.data || {};
  const g = activeData?.gridData ?? activeData?.grid_data ?? activeData?.grid;
  if (g === null || g === undefined) {
    return null;
  }
  if (typeof g === 'string') {
    try {
      return JSON.parse(g);
    } catch {
      return null;
    }
  }
  return g;
}

export function mergeEnvToBaseUrls(existing: any, env: any): any {
  const u = extractObject(env);
  if (!Object.keys(u).length) {
    return existing || {};
  }
  const base = { ...(existing || {}) };
  return {
    ...base,
    ...u,
    admin: u.admin ?? u.admin_api_url ?? base.admin ?? '',
    api: u.api ?? u.base_url ?? base.api ?? '',
    custom: u.custom ?? u.custom_api_url ?? base.custom ?? '',
    efile: u.efile ?? u.efile_api_url ?? base.efile ?? '',
    pdfEngine: u.pdfEngine ?? base.pdfEngine ?? '',
    bulkPdfEngine: u.bulkPdfEngine ?? base.bulkPdfEngine ?? '',
  };
}

export function generalConfigFromGlobal(globalService: GlobalService): any {
  const gc = extractObject(globalService.GENERAL_CONFIG || {});
  return {
    ...gc,
    base_url: gc.base_url ?? gc.api ?? '/gtw',
    admin: gc.admin ?? gc.admin_api_url ?? '/admin',
    api: gc.api ?? gc.base_url ?? '/gtw',
    custom: gc.custom ?? gc.custom_api_url ?? '/custom',
    efile: gc.efile ?? gc.efile_api_url ?? '/gtw-efile-api',
    pdfEngine: gc.pdfEngine ?? gc.pdf_engine_api_url ?? '/pdf-engine',
    bulkPdfEngine: gc.bulkPdfEngine ?? gc.bulk_pdf_engine_api_url ?? '/bulk-pdf-engine',
  };
}

export function hydratedGlobalParams(
  globalService: GlobalService,
  workspaceService: WorkspaceService
): any {
  const activeScreen = workspaceService.workspaceFactory?.activeScreen || {};
  const fp = normalizeFilterParams(activeScreen);
  return globalService.hydrateGlobalParamsFromContext(activeScreen, fp);
}

/** Shape compatible with legacy `header-data` consumers (subset). */
export function buildHeaderDataShell(
  globalService: GlobalService,
  appService: AppService
): any {
  const gc = generalConfigFromGlobal(globalService);
  return {
    generalConfigData: {
      ...gc,
      base_url: gc.base_url ?? gc.api ?? '',
    },
    userSettingsData: appService.USER_SETTINGS || {},
    serverConfigData: appService.SERVER_CONFIG || {},
    globalParams: extractObject(globalService.global?.globalParams || {}),

    // Method delegates required by JsonObjectFactoryService and other legacy consumers
    getScenario: (tax_year: any) => globalService.getScenario(tax_year),
    getEIN: () => globalService.getEIN(),
    getTAXYEAR: () => globalService.getTAXYEAR(),
    getChartType: () => globalService.getChartType(),
    get inputs() { return globalService.global?.inputs || {}; },
  };
}

/**
 * Sanitize action parameters: remove action_id when action_code is present.
 * Prevents conflicts between globalParams.action_id and explicitly set action_code.
 * Applied globally to ensure consistency across all components and services.
 */
export function sanitizeActionParams(params: any): any {
  if (!params || typeof params !== 'object') {
    return params;
  }
  if (params.action_code) {
    const cleaned = { ...params };
    delete cleaned.action_id;
    return cleaned;
  }
  return params;
}

/**
 * Admin-aware resolve helpers.
 * These check an admin screen context first (when present), then fall back to
 * workspace activeScreen.  The `adminCtx` parameter uses a duck-typed contract
 * `{ hasContext: boolean; rowData: any; gridData: any }` so that
 * workspace-element-context.ts does not import from admin/ (avoids circular deps).
 */

export function resolveRowPayload(adminCtx: any, ws: WorkspaceService): any {
  if (adminCtx?.hasContext) {
    const rd = adminCtx.rowData;
    return Array.isArray(rd) ? rd : extractObject(rd);
  }
  return rowPayloadFromActiveScreen(ws.workspaceFactory?.activeScreen || {});
}

export function resolveGridPayload(adminCtx: any, ws: WorkspaceService): any {
  if (adminCtx?.hasContext) {
    return adminCtx.gridData;
  }
  return gridPayloadFromActiveScreen(ws.workspaceFactory?.activeScreen || {});
}

export function resolveFilterParams(adminCtx: any, ws: WorkspaceService): any {
  if (adminCtx?.hasContext) {
    return {};
  }
  return normalizeFilterParams(ws.workspaceFactory?.activeScreen || {});
}

export function resolveGlobalParams(adminCtx: any, gs: GlobalService, ws: WorkspaceService): any {
  if (adminCtx?.hasContext) {
    return gs.global?.globalParams || {};
  }
  return hydratedGlobalParams(gs, ws);
}

/**
 * Single-row consumers (forms/modals): when `rowPayloadFromActiveScreen` returns an array of
 * grid selections (Gate 4), use the first row. Grid cells may be `{ data: row }` or a plain row.
 * DST admin components assign `rowPayloadFromActiveScreen` as-is; use this helper when the UI
 * requires one object (e.g. spread into `rowData` for CRUD).
 */
export function firstGridRowObjectFromPayload(raw: any): any {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (first && typeof first === 'object') {
      return first.data != null && typeof first.data === 'object' ? first.data : first;
    }
    return {};
  }
  if (raw && typeof raw === 'object') {
    return raw;
  }
  return {};
}
